// apps/admin/src/routes/templates/TemplatesList.tsx
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useSession } from "../../lib/auth";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { StatusBadge } from "../../components/StatusBadge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Skeleton,
  useToast,
} from "@jourfx/ui";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Sparkles,
  ShieldAlert,
  LayoutTemplate,
  AlertTriangle,
} from "lucide-react";
import { Link } from "@tanstack/react-router";


interface AppointmentTemplate {
  _id: string;
  organizationId: string;
  name: string;
  description?: string;
  duration: number;
  color: string;
  requiresAssessment: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
  usageCount?: number;
}

interface OrganizationOption {
  _id: string;
  name: string;
}

interface DurationPolicy {
  allowedDurations: number[];
  appointmentDuration: number;
}

interface TemplateFormState {
  name: string;
  description: string;
  duration: string;
  color: string;
  requiresAssessment: boolean;
  sortOrder: string;
}

interface DurationPolicyFormState {
  allowedDurationsInput: string;
  appointmentDuration: string;
}

const emptyForm: TemplateFormState = {
  name: "",
  description: "",
  duration: "",
  color: "#3b82f6",
  requiresAssessment: false,
  sortOrder: "0",
};

function formatDurationListInput(durations: number[]) {
  return durations.join(", ");
}

function parseDurationListInput(input: string) {
  const parts = input
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    return { durations: [] as number[], errorKey: "durationPolicy.validation.empty" };
  }

  const durations = parts.map((part) => Number(part));
  const hasInvalidDuration = durations.some(
    (duration) => !Number.isInteger(duration) || duration <= 0
  );
  if (hasInvalidDuration) {
    return { durations: [] as number[], errorKey: "durationPolicy.validation.invalid" };
  }

  if (new Set(durations).size !== durations.length) {
    return { durations: [] as number[], errorKey: "durationPolicy.validation.duplicates" };
  }

  return {
    durations: [...durations].sort((left, right) => left - right),
    errorKey: null,
  };
}

function TemplateFormDialog({
  open,
  onOpenChange,
  onSubmit,
  onClose,
  durationPolicy,
  initialValue,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: TemplateFormState) => void;
  onClose: () => void;
  durationPolicy: DurationPolicy;
  initialValue?: TemplateFormState | null;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation("templates");
  const { t: tc } = useTranslation("common");
  const [form, setForm] = useState<TemplateFormState>(initialValue ?? emptyForm);
  const [touched, setTouched] = useState(false);
  const initialDuration = initialValue ? Number(initialValue.duration) : null;
  const hasLegacyInitialDuration = Boolean(
    initialDuration && !durationPolicy.allowedDurations.includes(initialDuration)
  );
  const durationOptions = useMemo(() => {
    const options = [...durationPolicy.allowedDurations];
    if (initialDuration && !options.includes(initialDuration)) {
      options.push(initialDuration);
    }
    return options.sort((a, b) => a - b);
  }, [durationPolicy.allowedDurations, initialDuration]);

  useEffect(() => {
    if (open) {
      setForm(initialValue ?? emptyForm);
      setTouched(false);
    }
  }, [initialValue, open]);

  const reset = () => {
    setForm(initialValue ?? emptyForm);
    setTouched(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
      onClose();
    }
    onOpenChange(next);
  };

  const selectedDuration = Number(form.duration);
  const isSelectedDurationAllowed =
    form.duration.trim().length > 0 &&
    durationPolicy.allowedDurations.includes(selectedDuration);
  const isValid = form.name.trim().length > 0 &&
    isSelectedDurationAllowed &&
    /^#[0-9a-fA-F]{6}$/.test(form.color.trim());

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {initialValue ? t("form.editTitle") : t("form.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("form.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("form.name")} *</Label>
            <Input
              value={form.name}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, name: e.target.value }));
                setTouched(true);
              }}
              placeholder={t("form.namePlaceholder")}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("form.descriptionLabel")}</Label>
            <Input
              value={form.description}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, description: e.target.value }));
                setTouched(true);
              }}
              placeholder={t("form.descriptionPlaceholder")}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("form.duration")} *</Label>
              <select
                value={form.duration}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, duration: e.target.value }));
                  setTouched(true);
                }}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="template-duration-select"
              >
                <option value="">{t("form.durationPlaceholder")}</option>
                {durationOptions.map((duration) => (
                  <option key={duration} value={duration}>
                    {duration === initialDuration &&
                    !durationPolicy.allowedDurations.includes(duration)
                      ? t("form.durationLegacyOption", { count: duration })
                      : t("form.durationMinutes", { count: duration })}
                  </option>
                ))}
              </select>
              {hasLegacyInitialDuration && (
                <p
                  className="text-xs text-warning"
                  data-testid="template-legacy-duration-note"
                >
                  {t("form.legacyDurationHint")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t("form.color")} *</Label>
              <Input
                type="text"
                value={form.color}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, color: e.target.value }));
                  setTouched(true);
                }}
                placeholder="#3b82f6"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("form.sortOrder")}</Label>
            <Input
              type="number"
              value={form.sortOrder}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, sortOrder: e.target.value }));
                setTouched(true);
              }}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <p className="text-sm font-medium">{t("form.requiresAssessment")}</p>
              <p className="text-xs text-muted-foreground">
                {t("form.requiresAssessmentHint")}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <span className="sr-only">{t("form.requiresAssessment")}</span>
              <input
                type="checkbox"
                checked={form.requiresAssessment}
                onChange={(event) => {
                  setForm((prev) => ({
                    ...prev,
                    requiresAssessment: event.target.checked,
                  }));
                  setTouched(true);
                }}
                className="h-4 w-4 rounded border border-input text-primary focus:ring-2 focus:ring-ring"
              />
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {tc("actions.cancel")}
          </Button>
          <Button
            onClick={() => onSubmit(form)}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? t("form.saving") : tc("actions.save")}
          </Button>
        </DialogFooter>
        {!isValid && touched && (
          <p className="text-xs text-error">{t("form.validationError")}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TemplatesList() {
  return <TemplatesListContent />;
}

function TemplatesListContent() {
  const { t } = useTranslation("templates");
  const { t: tc } = useTranslation("common");
  const { toast } = useToast();
  const session = useSession();
  const canSelectOrganization = session.isAdministrator || session.isSuperAdmin;

  const [search, setSearch] = useState("");
  const [orgId, setOrgId] = useState<string | null>(session.organizationId ?? null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<AppointmentTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<AppointmentTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [policyForm, setPolicyForm] = useState<DurationPolicyFormState>({
    allowedDurationsInput: "",
    appointmentDuration: "",
  });
  const [isSavingPolicy, setIsSavingPolicy] = useState(false);

  const organizations = useQuery(
    api.admin.organizations.list,
    canSelectOrganization
      ? {
          status: "active",
          limit: 100,
        }
      : "skip"
  );

  const selectedOrgId = orgId;

  const templates = useQuery(
    (api as any).admin.appointmentTemplates.list,
    selectedOrgId ? { organizationId: selectedOrgId as any } : "skip"
  );

  const settings = useQuery(
    (api as any).admin.appointmentTemplates.getDurationPolicyForOrganization,
    selectedOrgId ? { organizationId: selectedOrgId as any } : "skip"
  );

  const updateDurationPolicy = useMutation(
    (api as any).admin.appointmentTemplates.updateDurationPolicy
  );
  const createTemplate = useMutation((api as any).admin.appointmentTemplates.create);
  const updateTemplate = useMutation((api as any).admin.appointmentTemplates.update);
  const deactivateTemplate = useMutation((api as any).admin.appointmentTemplates.deactivate);
  const activateTemplate = useMutation((api as any).admin.appointmentTemplates.activate);
  const deleteTemplate = useMutation((api as any).admin.appointmentTemplates.deleteHard);
  const createDefaults = useMutation((api as any).admin.appointmentTemplates.createDefaults);

  const orgOptions: OrganizationOption[] =
    (organizations?.organizations as OrganizationOption[]) || [];
  const visibleOrgOptions = useMemo(() => {
    if (session.isSuperAdmin) {
      return orgOptions;
    }
    if (!session.organizationId) {
      return orgOptions;
    }
    return orgOptions.filter((org) => org._id === session.organizationId);
  }, [orgOptions, session.isSuperAdmin, session.organizationId]);

  const durationPolicy = useMemo<DurationPolicy>(() => {
    const policy = settings as DurationPolicy | undefined;
    if (!policy?.allowedDurations?.length) {
      return {
        allowedDurations: [30, 60, 90],
        appointmentDuration: 30,
      };
    }

    return policy;
  }, [settings]);
  const durationPolicyKey = useMemo(
    () =>
      `${durationPolicy.allowedDurations.join(",")}:${durationPolicy.appointmentDuration}`,
    [durationPolicy.allowedDurations, durationPolicy.appointmentDuration]
  );

  useEffect(() => {
    if (!selectedOrgId) {
      setPolicyForm((current) =>
        current.allowedDurationsInput === "" && current.appointmentDuration === ""
          ? current
          : {
              allowedDurationsInput: "",
              appointmentDuration: "",
            }
      );
      return;
    }

    const nextPolicyForm = {
      allowedDurationsInput: formatDurationListInput(durationPolicy.allowedDurations),
      appointmentDuration: String(durationPolicy.appointmentDuration),
    };
    setPolicyForm((current) =>
      current.allowedDurationsInput === nextPolicyForm.allowedDurationsInput &&
      current.appointmentDuration === nextPolicyForm.appointmentDuration
        ? current
        : nextPolicyForm
    );
  }, [selectedOrgId, durationPolicyKey]);

  const templateList: AppointmentTemplate[] = (templates as AppointmentTemplate[]) || [];
  const parsedDurationPolicyInput = useMemo(
    () => parseDurationListInput(policyForm.allowedDurationsInput),
    [policyForm.allowedDurationsInput]
  );
  const durationPolicyOptions = parsedDurationPolicyInput.errorKey
    ? durationPolicy.allowedDurations
    : parsedDurationPolicyInput.durations;
  const durationPolicyError = parsedDurationPolicyInput.errorKey
    ? t(parsedDurationPolicyInput.errorKey)
    : policyForm.appointmentDuration.trim().length === 0
      ? t("durationPolicy.validation.defaultRequired")
      : !durationPolicyOptions.includes(Number(policyForm.appointmentDuration))
        ? t("durationPolicy.validation.defaultNotAllowed")
        : null;
  const isPolicyDirty =
    JSON.stringify(durationPolicy.allowedDurations) !==
      JSON.stringify(parsedDurationPolicyInput.errorKey ? [] : parsedDurationPolicyInput.durations) ||
    Number(policyForm.appointmentDuration) !== durationPolicy.appointmentDuration;
  const canSavePolicy =
    Boolean(selectedOrgId) &&
    !durationPolicyError &&
    isPolicyDirty &&
    !isSavingPolicy;

  const filteredTemplates = templateList.filter((tpl) =>
    tpl.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setFormOpen(true);
  };

  const handleSubmit = async (values: TemplateFormState) => {
    if (!selectedOrgId) {
      toast({
        title: t("toasts.selectOrg.title"),
        description: t("toasts.selectOrg.description"),
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      if (editingTemplate) {
        const patch: Record<string, unknown> = {};
        if (values.name !== editingTemplate.name) patch.name = values.name;
        if ((values.description || "") !== (editingTemplate.description || "")) {
          patch.description = values.description || undefined;
        }
        if (Number(values.duration) !== editingTemplate.duration) {
          patch.duration = Number(values.duration);
        }
        if (values.color !== editingTemplate.color) patch.color = values.color;
        if (values.requiresAssessment !== editingTemplate.requiresAssessment) {
          patch.requiresAssessment = values.requiresAssessment;
        }
        if (Number(values.sortOrder || 0) !== (editingTemplate.sortOrder || 0)) {
          patch.sortOrder = Number(values.sortOrder || 0);
        }

        await updateTemplate({
          templateId: editingTemplate._id as any,
          patch,
        });
        toast({
          title: t("toasts.updated.title"),
          description: t("toasts.updated.description", { name: values.name }),
        });
      } else {
        await createTemplate({
          organizationId: selectedOrgId as any,
          name: values.name,
          description: values.description || undefined,
          duration: Number(values.duration),
          color: values.color,
          requiresAssessment: values.requiresAssessment,
          sortOrder: Number(values.sortOrder || 0),
        });
        toast({
          title: t("toasts.created.title"),
          description: t("toasts.created.description", { name: values.name }),
        });
      }
      setFormOpen(false);
    } catch (error) {
      toast({
        title: t("toasts.error"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (tpl: AppointmentTemplate) => {
    try {
      if (tpl.isActive) {
        await deactivateTemplate({ templateId: tpl._id as any });
        toast({
          title: t("toasts.deactivated.title"),
          description: t("toasts.deactivated.description", { name: tpl.name }),
        });
      } else {
        await activateTemplate({ templateId: tpl._id as any });
        toast({
          title: t("toasts.activated.title"),
          description: t("toasts.activated.description", { name: tpl.name }),
        });
      }
    } catch (error) {
      toast({
        title: t("toasts.error"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    setIsDeleting(true);
    try {
      const result = await deleteTemplate({ templateId: templateToDelete._id as any });
      toast({
        title: t("toasts.deleted.title"),
        description: t("toasts.deleted.description", { count: result?.affectedAppointments || 0 }),
      });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      toast({
        title: t("toasts.error"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateDefaults = async () => {
    if (!selectedOrgId) return;
    try {
      const result = await createDefaults({ organizationId: selectedOrgId as any });
      if (result?.created) {
        toast({
          title: t("toasts.defaultsCreated.title"),
          description: t("toasts.defaultsCreated.description"),
        });
      } else {
        toast({
          title: t("toasts.defaultsExist.title"),
          description: t("toasts.defaultsExist.description"),
        });
      }
    } catch (error) {
      toast({
        title: t("toasts.error"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    }
  };

  const handleSaveDurationPolicy = async () => {
    if (!selectedOrgId || durationPolicyError || parsedDurationPolicyInput.errorKey) {
      return;
    }

    setIsSavingPolicy(true);
    try {
      await updateDurationPolicy({
        organizationId: selectedOrgId as any,
        allowedDurations: parsedDurationPolicyInput.durations,
        appointmentDuration: Number(policyForm.appointmentDuration),
      });
      toast({
        title: t("toasts.durationPolicyUpdated.title"),
        description: t("toasts.durationPolicyUpdated.description"),
      });
    } catch (error) {
      toast({
        title: t("toasts.error"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSavingPolicy(false);
    }
  };

  const formInitial = editingTemplate
    ? {
        name: editingTemplate.name,
        description: editingTemplate.description || "",
        duration: String(editingTemplate.duration),
        color: editingTemplate.color,
        requiresAssessment: editingTemplate.requiresAssessment,
        sortOrder: String(editingTemplate.sortOrder ?? 0),
      }
    : null;

  if (session.isPending || (templates === undefined && selectedOrgId)) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!session.isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <ShieldAlert className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">{tc("auth.loginRequired")}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {tc("auth.loginRequiredMessage")}
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {tc("auth.toLogin")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
        actions={
          <Button onClick={handleOpenCreate} disabled={!selectedOrgId} data-testid="templates-create">
            <Plus className="mr-2 h-4 w-4" />
            {t("createTemplate")}
          </Button>
        }
      />

      {canSelectOrganization && (
        <Card>
          <CardHeader>
            <CardTitle>{t("selectOrg.title")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <select
              value={orgId ?? ""}
              onChange={(e) => setOrgId(e.target.value || null)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="templates-org-select"
              disabled={Boolean(session.isAdministrator && session.organizationId)}
            >
              <option value="">{t("selectOrg.placeholder")}</option>
              {visibleOrgOptions.map((org) => (
                <option key={org._id} value={org._id}>
                  {org.name}
                </option>
              ))}
            </select>
            <p className="text-sm text-muted-foreground">
              {t("selectOrg.hint")}
            </p>
          </CardContent>
        </Card>
      )}

      {selectedOrgId && (
        <Card data-testid="duration-policy-card">
          <CardHeader>
            <CardTitle>{t("durationPolicy.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("durationPolicy.description")}
            </p>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
              <div className="space-y-2">
                <Label>{t("durationPolicy.allowedDurationsLabel")}</Label>
                <Input
                  value={policyForm.allowedDurationsInput}
                  onChange={(event) =>
                    setPolicyForm((current) => ({
                      ...current,
                      allowedDurationsInput: event.target.value,
                    }))
                  }
                  placeholder={t("durationPolicy.allowedDurationsPlaceholder")}
                  data-testid="duration-policy-input"
                />
                <p className="text-xs text-muted-foreground">
                  {t("durationPolicy.allowedDurationsHint")}
                </p>
              </div>

              <div className="space-y-2">
                <Label>{t("durationPolicy.defaultDurationLabel")}</Label>
                <select
                  value={policyForm.appointmentDuration}
                  onChange={(event) =>
                    setPolicyForm((current) => ({
                      ...current,
                      appointmentDuration: event.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="duration-policy-default"
                >
                  <option value="">{t("durationPolicy.defaultDurationPlaceholder")}</option>
                  {durationPolicyOptions.map((duration) => (
                    <option key={duration} value={duration}>
                      {t("form.durationMinutes", { count: duration })}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{t("durationPolicy.currentPolicyLabel")}</span>
              {durationPolicy.allowedDurations.map((duration) => (
                <Badge key={duration} variant="secondary">
                  {t("form.durationMinutes", { count: duration })}
                </Badge>
              ))}
              <Badge variant="outline">
                {t("durationPolicy.defaultBadge", {
                  count: durationPolicy.appointmentDuration,
                })}
              </Badge>
            </div>

            {durationPolicyError && (
              <div
                className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm text-warning"
                data-testid="duration-policy-error"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{durationPolicyError}</span>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={handleSaveDurationPolicy}
                disabled={!canSavePolicy}
                data-testid="duration-policy-save"
              >
                {isSavingPolicy
                  ? t("durationPolicy.saving")
                  : t("durationPolicy.save")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedOrgId && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="templates-search"
            />
          </div>
          <Button variant="outline" onClick={handleCreateDefaults} data-testid="templates-create-defaults">
            <Sparkles className="mr-2 h-4 w-4" />
            {t("createDefaults")}
          </Button>
        </div>
      )}

      {!selectedOrgId ? (
        <div data-testid="templates-empty">
          <EmptyState
            icon={LayoutTemplate}
            title={t("empty.selectOrg")}
            description={t("empty.selectOrgDescription")}
          />
        </div>
      ) : filteredTemplates.length === 0 && selectedOrgId ? (
        <div data-testid="templates-empty">
          <EmptyState
            icon={LayoutTemplate}
            title={search ? tc("table.noResults") : t("empty.noTemplates")}
            description={
              search
                ? t("empty.noSearchResults")
                : t("empty.noTemplatesHint")
            }
            action={
              search
                ? undefined
                : {
                    label: t("createDefaults"),
                    onClick: handleCreateDefaults,
                    icon: Sparkles,
                  }
            }
          />
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden" data-testid="templates-list">
          <div className="divide-y divide-border">
            {filteredTemplates.map((tpl) => (
              <div
                key={tpl._id}
                className="flex flex-col gap-4 px-6 py-4 sm:flex-row sm:items-center"
                data-testid="template-row"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: tpl.color }} />
                  <div>
                    <p className="font-medium text-foreground">{tpl.name}</p>
                    <p className="text-xs text-muted-foreground">{tpl.description || t("row.noDescription")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{t("row.minutes", { count: tpl.duration })}</Badge>
                  {tpl.requiresAssessment && <Badge>Assessment</Badge>}
                  <Badge variant="outline">{t("row.appointments", { count: tpl.usageCount || 0 })}</Badge>
                </div>
                <StatusBadge status={tpl.isActive ? "active" : "inactive"} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        setEditingTemplate(tpl);
                        setFormOpen(true);
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      {t("actions.edit")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleToggleActive(tpl)}>
                      {tpl.isActive ? (
                        <ToggleLeft className="mr-2 h-4 w-4" />
                      ) : (
                        <ToggleRight className="mr-2 h-4 w-4" />
                      )}
                      {tpl.isActive ? t("actions.deactivate") : t("actions.activate")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => {
                        setTemplateToDelete(tpl);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("actions.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </div>
      )}

      <TemplateFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmit={handleSubmit}
        onClose={() => {
          setFormOpen(false);
          setEditingTemplate(null);
        }}
        durationPolicy={durationPolicy}
        initialValue={formInitial}
        isSubmitting={isSaving}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {templateToDelete
                ? t("deleteDialog.descriptionWithName", {
                    name: templateToDelete.name,
                    count: templateToDelete.usageCount || 0,
                  })
                : t("deleteDialog.descriptionGeneric")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? t("form.deleting") : tc("actions.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
